import { ArdoqId, APIFieldAttributes, APIEntityType } from '@ardoq/api-types';
import { createFifoCache, isTruthy } from '@ardoq/common-helpers';
import {
  ComponentLabelSource,
  LabelFormattingInfo,
  ReferenceLabelSource,
  COMPONENT_OR_REFERENCE_DESCRIPTION_FIELD_LABEL,
} from '@ardoq/data-model';
import { MultiLabelStyle, OtherLabel } from '../types';
import { DEFAULT_MULTI_LABEL_STYLE } from './multiLabelStyles';
import { dateRangeOperations } from '@ardoq/date-range';
import { LabelsInterface } from '../labelsInterface/labelsInterface';
import { fieldValueFormatters } from '@ardoq/scope-data';
import { type I18nLocale, t } from '@ardoq/i18n';

type FieldLabelArgs = {
  labelsInterface: LabelsInterface;
  modelId: ArdoqId;
  showFieldName: boolean;
  entityFields: APIFieldAttributes[];
  entityType?: APIEntityType;
  multiLabelStyle?: MultiLabelStyle;
  i18nLocale: I18nLocale;
};

const labelSources = {
  [ComponentLabelSource.TYPE]: {
    entityType: APIEntityType.COMPONENT,
    getFieldLabelAndValue: (
      labelsInterface: LabelsInterface,
      id: string,
      i18nLocale: I18nLocale
    ) => ({
      label: t(
        i18nLocale,
        'graph:labelFormatting.componentType',
        'Component type'
      ),
      value: labelsInterface.getComponentTypeName(id),
    }),
  },
  [ReferenceLabelSource.DISPLAY_TEXT]: {
    entityType: APIEntityType.REFERENCE,
    getFieldLabelAndValue: (
      labelsInterface: LabelsInterface,
      id: string,
      i18nLocale: I18nLocale
    ) => ({
      label: t(i18nLocale, 'graph:labelFormatting.displayText', 'Display text'),
      value: labelsInterface.getReferenceDisplayText(id),
    }),
  },
} as const;

/**
 * We need to dash-format the date range labels according to https://ardoqcom.atlassian.net/browse/ARD-24627.
 * Since regex and string manipulation on every node is a perf concern, we cache the results and
 * make sure the isDateRangeField  check is performing as fast as possible
 */
const dashFormatDateRangeFieldLabel = createFifoCache(
  100,
  dateRangeOperations.dashFormatDateRangeFieldLabel
);

export const fieldLabel =
  ({
    modelId,
    showFieldName,
    entityFields,
    entityType,
    labelsInterface,
    multiLabelStyle,
    i18nLocale,
  }: FieldLabelArgs) =>
  (fieldName: string): OtherLabel | null => {
    if (fieldName === COMPONENT_OR_REFERENCE_DESCRIPTION_FIELD_LABEL) {
      const getDescription =
        entityType === APIEntityType.COMPONENT
          ? labelsInterface.getComponentDescription
          : labelsInterface.getReferenceDescription;

      const description = fieldValueFormatters.formatTextAreaFieldValue(
        getDescription(modelId)
      );

      return description
        ? [
            t(i18nLocale, 'graph:labelFormatting.description', 'Description'),
            description,
            showFieldName,
            multiLabelStyle ?? DEFAULT_MULTI_LABEL_STYLE,
          ]
        : null;
    }

    const labelSource = labelSources[fieldName as keyof typeof labelSources];
    if (labelSource && labelSource.entityType === entityType) {
      const { label, value } = labelSource.getFieldLabelAndValue(
        labelsInterface,
        modelId,
        i18nLocale
      );
      return value
        ? [
            label,
            value,
            showFieldName,
            multiLabelStyle ?? DEFAULT_MULTI_LABEL_STYLE,
          ]
        : null;
    }
    const fieldData = entityFields.find(field => field.name === fieldName);
    if (!fieldData) {
      return null;
    }
    const { label: fieldLabel, _id: fieldId } = fieldData;

    const formattedFieldLabel =
      dateRangeOperations.fieldLabelIsPartOfDateRangeField(fieldLabel)
        ? dashFormatDateRangeFieldLabel(fieldLabel)
        : fieldLabel;

    const fieldValue = labelsInterface.formatFieldValueForComponentOrReference(
      fieldId,
      modelId
    );
    if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
      return null;
    }
    return [
      formattedFieldLabel,
      fieldValue,
      showFieldName,
      multiLabelStyle ?? DEFAULT_MULTI_LABEL_STYLE,
    ];
  };

type LabelFormattingLabelsArgs = {
  labelsInterface: LabelsInterface;
  modelId: ArdoqId;
  entityFields: APIFieldAttributes[];
  i18nLocale: I18nLocale;
  entityType?: APIEntityType;
  multiLabelStyle?: MultiLabelStyle;
};

const labelFormattingLabels =
  ({
    labelsInterface,
    modelId,
    entityFields,
    i18nLocale,
    entityType,
    multiLabelStyle,
  }: LabelFormattingLabelsArgs) =>
  (labelFormatting: LabelFormattingInfo) => {
    const { fields, showFieldName } = labelFormatting;
    return fields
      .map(
        fieldLabel({
          labelsInterface,
          entityFields,
          modelId,
          showFieldName,
          entityType,
          multiLabelStyle,
          i18nLocale,
        })
      )
      .filter(isTruthy);
  };

export { labelFormattingLabels };
